// Must be first: installs runtime polyfills before any dependency (e.g. pdfjs) loads.
import "./polyfills";
import { Authenticator } from "@aws-amplify/ui-react";
import { getDefaultStore, Provider as JotaiProvider } from "jotai";
import React, { Suspense } from "react";
import ReactDOM from "react-dom/client";
import { ErrorBoundary } from "react-error-boundary";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import "@aws-amplify/ui-react/styles.css";
import * as Sentry from "@sentry/react";
// Complete pending impersonation sign-in before React renders.
// This runs after a page reload triggered by startImpersonation(), which cleared
// Cognito tokens and stored challenge info in sessionStorage. We must complete the
// CUSTOM_AUTH signIn here (before Authenticator detects "no session" and shows login).
import { completePendingImpersonation } from "~/hooks/useImpersonation";
// initialize amplify client singleton
import "~/lib/amplify-client";
import { errorReporter } from "./error-handling";
import {
  DefaultFallbackComponent,
  defaultHandleError,
} from "./error-handling/DefaultFallbackComponent";
import { DesignSystemEffectManager } from "./hooks/useDesignSystemToggle";
import "./index.css";
import { OutOfSyncModal } from "./components/OutOfSyncModal";
import { routes } from "./routes";
import { setupChunkLoadRecovery } from "./services/chunkLoadRecovery";
import {
  applyFeatureFlagOverridesFromURL,
  FeatureFlagProvider,
} from "./services/featureFlags";

errorReporter.init({ enableReactRouterIntegration: true });
errorReporter.setupGlobalHandlers();
setupChunkLoadRecovery();

// Apply feature flag overrides from URL query parameters (must run early in app init)
applyFeatureFlagOverridesFromURL();

const pendingImpersonation = completePendingImpersonation();

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement,
);

// Wrap the createBrowserRouter function with Sentry's wrapCreateBrowserRouterV6
// This will automatically track navigation events and errors
const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouterV6(createBrowserRouter);
const router = sentryCreateBrowserRouter(routes);

// Defer rendering until impersonation sign-in completes (if pending).
// This prevents the Authenticator from flashing the login page.
void pendingImpersonation.then((wasPending) => {
  if (wasPending) return; // completePendingImpersonation does its own navigation

  root.render(
    <React.StrictMode>
      <ErrorBoundary
        FallbackComponent={DefaultFallbackComponent}
        onError={defaultHandleError}
      >
        <JotaiProvider store={getDefaultStore()}>
          <FeatureFlagProvider>
            <Authenticator.Provider>
              <DesignSystemEffectManager />
              <OutOfSyncModal />
              <Suspense fallback={null}>
                <RouterProvider router={router} />
              </Suspense>
            </Authenticator.Provider>
          </FeatureFlagProvider>
        </JotaiProvider>
      </ErrorBoundary>
    </React.StrictMode>,
  );
});
